import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const STATS = [
  {
    stage: "STAGE 01 · WANT IT",
    big: "77%",
    claim: "of inactive Indians want to become active in the next 6 months.",
    body: "A pan-India survey of 3,004 consumers aged 18–62, alongside wider research across 40+ cities, found that 77% of currently inactive consumers were willing to switch to an active fitness lifestyle.",
    source: "INDIA FITNESS MARKET REPORT 2025 · DELOITTE INDIA × HEALTH & FITNESS ASSOCIATION",
    href: "https://www.healthandfitness.org/wp-content/uploads/India-Fitness-Market-2025_FINAL_Print.pdf",
  },
  {
    stage: "STAGE 02 · START IT",
    big: "47.6%",
    claim: "of people who intend to be active fail to turn intention into behaviour.",
    body: "Nearly 1 in 2 people who intend to exercise still fail to follow through. Systematic review and meta-analysis of 29,600 participants across 25 independent samples.",
    source: "FEIL, FRITSCH & RHODES (2023) · BRITISH JOURNAL OF SPORTS MEDICINE",
    href: "https://doi.org/10.1136/bjsports-2022-106640",
  },
  {
    stage: "STAGE 03 · SUSTAIN IT",
    big: "10.1%",
    claim: "of fitness-app beginners remained adherent after a full year.",
    body: "In a cohort of 389,481 adult app users · including 100,709 beginners · only about 1 in 10 beginners stayed adherent at 12 months. Median time to dropout: 19 weeks.",
    source: "FRONTIERS IN SPORTS & ACTIVE LIVING (2026)",
    href: "https://www.frontiersin.org/journals/sports-and-active-living/articles/10.3389/fspor.2026.1855668/full",
  },
];

const ConsistencyProblem = () => (
  <section
    data-testid="consistency-problem-section"
    className="relative px-6 py-24 md:px-12 md:py-32"
  >
    <div className="mx-auto max-w-[1500px]">
      <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
        03 · THE PROBLEM
      </p>
      <h2
        data-testid="consistency-headline"
        className="mt-4 max-w-2xl font-display text-2xl font-light leading-snug text-white md:text-3xl"
      >
        Knowing what to do was never the problem.
        <br />
        <span className="text-signal">Doing it</span> is.
      </h2>

      <div className="mt-12 grid gap-5 lg:grid-cols-3">
        {STATS.map((s, i) => (
          <motion.div
            key={s.stage}
            data-testid={`consistency-stat-${i + 1}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.12 * i, ease: [0.16, 1, 0.3, 1] }}
            className={`flex flex-col rounded-xl border border-line bg-ink-2/50 p-6 md:p-8 ${
              i === 1 ? "lg:mt-6" : i === 2 ? "lg:mt-12" : ""
            }`}
          >
            <p className="font-mono text-[10px] tracking-[0.24em] text-signal/80">
              {s.stage}
            </p>
            <p className="mt-6 font-display text-5xl font-extralight tracking-tight text-white md:text-6xl">
              {s.big}
            </p>
            <p className="mt-4 font-display text-base font-light leading-snug text-white md:text-lg">
              {s.claim}
            </p>
            <p className="mt-4 text-xs leading-relaxed text-fog md:text-sm">
              {s.body}
            </p>
            <a
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`consistency-source-${i + 1}`}
              className="group mt-auto flex items-start gap-2 pt-6 font-mono text-[8px] leading-relaxed tracking-[0.14em] text-ash transition-colors duration-300 hover:text-fog"
            >
              <span>{s.source}</span>
              <ArrowUpRight size={11} className="mt-0.5 shrink-0 text-signal/60 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </motion.div>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.3 }}
        className="mx-auto mt-16 max-w-2xl text-center font-display text-xl font-light leading-relaxed text-fog md:text-2xl"
      >
        People want to change. Intention rarely becomes action. Consistency
        almost never survives.{" "}
        <span className="text-white">
          Trifit is engineered for exactly this gap.
        </span>
      </motion.p>
    </div>
  </section>
);

export default ConsistencyProblem;
